// GH-766 — image picker for banner fields (general banner page + company "באנר" tab).
// Uploads the file at ORIGINAL quality (POST setting/banner-image, no resize) and hands back the CDN URL;
// the caller stores that URL in the existing field. The URL can also be pasted/edited by hand.
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import constants from '../../constants';

// The axios default Authorization header is only set once the data provider has run, so a direct load of a
// custom page would call the API without it — send the token explicitly (same as Company/Departments.tsx).
export const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const uploadBannerImage = async (file: File): Promise<string> => {
	const formData = new FormData();
	formData.append('picture', file);
	const res = await axios.post(`${constants.API_URL}setting/banner-image`, formData, {
		headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() },
	});
	return res.data.imageUrl as string;
};

type Props = {
	label: string;
	hint?: string;
	value: string;
	onChange: (url: string) => void;
	disabled?: boolean;
};

const BannerImagePicker = ({ label, hint, value, onChange, disabled }: Props) => {
	const notify = useNotify();
	const inputRef = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);
	const [size, setSize] = useState<string>('');

	const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		try {
			setUploading(true);
			onChange(await uploadBannerImage(file));
			notify('התמונה הועלתה (באיכות מקורית) — לא לשכוח לשמור', { type: 'info' });
		} catch (err: any) {
			notify(err?.response?.data?.message || 'העלאת התמונה נכשלה', { type: 'error' });
		} finally {
			setUploading(false);
			if (inputRef.current) inputRef.current.value = '';
		}
	};

	return (
		<Box sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, background: '#fafafa' }}>
			<Typography variant="subtitle2">{label}</Typography>
			{hint && (
				<Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
					{hint}
				</Typography>
			)}
			{value ? (
				<Box sx={{ my: 1 }}>
					<img
						src={value}
						alt={label}
						style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 6, background: '#fff' }}
						onLoad={(e) => setSize(`${e.currentTarget.naturalWidth}×${e.currentTarget.naturalHeight}`)}
					/>
					{size && (
						<Typography variant="caption" color="textSecondary" display="block">
							{size} פיקסלים
						</Typography>
					)}
				</Box>
			) : (
				<Typography variant="caption" color="textSecondary" display="block" sx={{ my: 1 }}>
					אין תמונה
				</Typography>
			)}
			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
				<TextField
					size="small"
					fullWidth
					label="קישור לתמונה"
					value={value || ''}
					onChange={(e) => onChange(e.target.value)}
					disabled={disabled || uploading}
					inputProps={{ dir: 'ltr' }}
				/>
				<input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
				<Button
					variant="contained"
					onClick={() => inputRef.current?.click()}
					disabled={disabled || uploading}
					sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
				>
					{uploading ? <CircularProgress size={20} color="inherit" /> : 'העלאת תמונה'}
				</Button>
			</Box>
		</Box>
	);
};

export default BannerImagePicker;
